import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { GlobalStateContext } from 'contexts/global_state_context'
import { useEffectSkipFirst, useForm } from 'utils/hooks'
import moment from 'moment'
import { ThemeContext } from 'contexts/theme_context'
import { Table } from 'react-bootstrap'
import { ContentHeader } from 'components/shared/content'
import { SelectField } from 'components/shared/input'
import { selectYears, toDateText } from 'utils/util'
import { InstitutionSearchForm } from 'entities/institution'
import { useFetchInstitutionsApi } from 'api/institution'
import { SampleSearchForm } from 'entities/sample'
import { useFetchSamplesApi } from 'api/sample'
import { useFetchSalesAgenciesApi } from 'api/sales_agency'
import { SalesAgency } from 'entities/sales_agency'
import { FixedFooter } from 'components/shared/widget'
import { Flex } from 'components/shared/flex'

type Props = {
    history: H.History
}

type SalesAgencyPaymentSearchForm = {
    year?: string
    month?: string
    salesAgencyId?: number
}

const SalesAgencyPaymentPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const theme = useContext(ThemeContext)
    const form = useForm<SalesAgencyPaymentSearchForm>({ year: moment().format('YYYY'), month: moment().format('M') })
    const institutionsSearchForm = useForm<InstitutionSearchForm>({ ordering: 'institution_id' })
    const institutionsApi = useFetchInstitutionsApi(institutionsSearchForm)
    const samplesSearchForm = useForm<SampleSearchForm>({ ordering: 'sample_institution_id' })
    const samplesApi = useFetchSamplesApi(samplesSearchForm)
    const salesAgenciesApi = useFetchSalesAgenciesApi()

    useEffect(() => {
        salesAgenciesApi.execute()
    }, [])

    useEffectSkipFirst(() => {
        globalState.setLoading(samplesApi.loading)
    }, [samplesApi.loading])

    useEffectSkipFirst(() => {
        institutionsSearchForm.update((f) => {
            f.salesAgencyId = form.object.salesAgencyId
            f.contractDateAfter = `${form.object.year}-${form.object.month}-01`
            f.contractDateBefore = moment(`${form.object.year}-${form.object.month}-01`).endOf('month').format('YYYY-MM-DD')
        })
        samplesSearchForm.update((f) => {
            f.salesAgencyId = form.object.salesAgencyId
            f.deliveryDateAfter = `${form.object.year}-${form.object.month}-01`
            f.deliveryDateBefore = moment(`${form.object.year}-${form.object.month}-01`).endOf('month').format('YYYY-MM-DD')
        })
    }, [form.object])

    const salesAgency = (): SalesAgency | undefined => {
        return salesAgenciesApi.response.results.find((sa) => sa.id === form.object.salesAgencyId)
    }

    let totalMargin = 0
    return (
        <>
            <ContentHeader
                headerTitle="営業代行マージン支払額"
                right={
                    <>
                        <SelectField
                            form={form}
                            attr="salesAgencyId"
                            selectItems={salesAgenciesApi.response.results.map((sa) => {
                                return { label: sa.name, value: sa.id! }
                            })}
                            style={{ width: 120 }}
                            includeBlank
                        />
                        <SelectField form={form} attr="year" selectItems={selectYears()} style={{ width: 120 }} />
                        <SelectField
                            form={form}
                            attr="month"
                            selectItems={new Array(12).fill(0).map((y, idx) => {
                                const month = idx + 1
                                return { label: month + '月', value: month }
                            })}
                        />
                    </>
                }
            />
            {form.object.salesAgencyId && (
                <>
                    <h4>契約締結報酬</h4>
                    <div style={{ padding: 20, marginTop: 5 }} className="shadow">
                        <Table bordered>
                            <thead>
                                <tr>
                                    <th style={{ width: 250 }}>営業代行会社</th>
                                    <th style={{ minWidth: 200 }}>病院・クリニック</th>
                                    <th style={{ width: 150, textAlign: 'center' }}>契約日</th>
                                    <th style={{ width: 150, textAlign: 'right' }}>金額</th>
                                </tr>
                            </thead>
                            <tbody>
                                {institutionsApi.response.results.map((institution) => {
                                    return (
                                        <tr key={'institution-' + institution.id}>
                                            <td>{salesAgency()?.name}</td>
                                            <td>{institution.name}</td>
                                            <td style={{ textAlign: 'center' }}>{institution.contractDate}</td>
                                            <td style={{ textAlign: 'right' }}>{salesAgency()?.acquisitionReward?.toLocaleString()}円</td>
                                        </tr>
                                    )
                                })}
                                <tr>
                                    <td colSpan={3}>合計</td>
                                    <td style={{ textAlign: 'right' }}>
                                        {(institutionsApi.response.results.length * (salesAgency()?.acquisitionReward ?? 0)).toLocaleString()}円
                                    </td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>

                    <h4 style={{ marginTop: 20 }}>売上マージン</h4>
                    <div style={{ padding: 20, marginTop: 5 }} className="shadow">
                        <Table bordered>
                            <thead>
                                <tr>
                                    <th style={{ minWidth: 200 }}>病院・クリニック</th>
                                    <th style={{ width: 200 }}>検体コード</th>
                                    <th style={{ width: 150, textAlign: 'center' }}>検体受領日</th>
                                    <th style={{ width: 150, textAlign: 'right' }}>金額</th>
                                </tr>
                            </thead>
                            <tbody>
                                {samplesApi.response.results.map((sample) => {
                                    const margin = (sample.price! - (sample.discount ?? 0)) * (salesAgency()?.salesMargin ?? 0)
                                    totalMargin += margin
                                    return (
                                        <tr key={'sample-' + sample.id}>
                                            <td>{sample?.institution?.name}</td>
                                            <td>{sample?.code}</td>
                                            <td style={{ textAlign: 'center' }}>{toDateText(sample.deliveryDate)}</td>
                                            <td style={{ textAlign: 'right' }}>{margin.toLocaleString()}円</td>
                                        </tr>
                                    )
                                })}
                                <tr>
                                    <td colSpan={3}>合計</td>
                                    <td style={{ textAlign: 'right' }}>{totalMargin.toLocaleString()}円</td>
                                </tr>
                            </tbody>
                        </Table>
                    </div>
                    <FixedFooter open={samplesApi.isSuccess()}>
                        <Flex justifyContent="flex-end" style={{ width: '100%' }}>
                            <h3>
                                合計支払額: {(institutionsApi.response.results.length * (salesAgency()?.acquisitionReward ?? 0) + totalMargin).toLocaleString()}
                                円
                            </h3>
                        </Flex>
                    </FixedFooter>
                </>
            )}
        </>
    )
}

export default withRouter(SalesAgencyPaymentPage)
